import { uniqueId } from 'lodash';

import {
  FieldSchemaErrorEntity,
  InquiryTemplateVersionError,
  StepErrorEntity,
  UiComponentErrorEntity,
  WorkflowVersionErrorEntity,
  WorkflowVersionStepErrorEntity,
} from 'dashboard/lib/interfaces/InquiryTemplateVersionErrors';
import { createMockActionStep } from 'dashboard/lib/stubs/inquiry-workflow-version';
import { createMockVerificationTemplateVersionGovernmentId } from 'dashboard/lib/stubs/verification-template-version';
import { createMockWorkflowVersionV2 } from 'dashboard/lib/stubs/workflow-version';
import {
  CombinedStepBaseConfig,
  DeviceHandoffConfigLocalizations,
  InquiryTemplateVersion,
  InquiryTemplateVersionAttributes,
  InquiryTemplateVersionRelationships,
  InquiryTemplateVersionStatus,
  InquiryTemplateVersionStepComplete,
  InquiryTemplateVersionStepCompleteConfig,
  InquiryTemplateVersionStepDocument,
  InquiryTemplateVersionStepDocumentConfig,
  InquiryTemplateVersionStepGovernmentId,
  InquiryTemplateVersionStepGovernmentIdConfig,
  InquiryTemplateVersionStepSelfie,
  InquiryTemplateVersionStepSelfieConfig,
  InquiryTemplateVersionStepStatus,
  InquiryTemplateVersionStepUi,
  InquiryTemplateVersionStepUiConfig,
  InquiryTemplateVersionTier,
  PartialInquiryTemplateVersion,
  PartialInquiryTemplateVersionAttributes,
} from 'dashboard/models/inquiry-template-version';
import { InquiryTemplateSchema } from 'dashboard/redux/slices/inquiry-template';
import {
  createMockCompleteLocalizations,
  createMockPlatformLocalizations,
} from 'inquiry/lib/stubs/inquiry';
import { GOVERNMENT_ID_DESIGN_VERSION } from 'lib/constants/governmentIds';
import { FieldSchema, FieldSchemaType } from 'lib/fields';
import { ConfigValueDirection, StyleValueJustify } from 'lib/inquiryStyles';
import { ApiModel } from 'lib/interfaces/api';
import DeviceHandoffOption from 'lib/interfaces/DeviceHandoffOption';
import {
  ClientStepKeys,
  DeviceHandoffLocalizations,
  StepComplete,
  StepCompleteConfig,
  StepUi,
  StepUiConfig,
  UiStepType,
} from 'lib/interfaces/StepsConfig';
import {
  AssetSource,
  ButtonCompleteComponent,
  ButtonCompleteComponentConfig,
  ButtonSubmitComponent,
  ButtonSubmitComponentConfig,
  ButtonType,
  ImageHostedComponentConfig,
  ImageKey,
  InputCheckboxComponentConfig,
  InputCountrySelectComponentConfig,
  InputDateComponentConfig,
  InputFileUploadComponentConfig,
  InputInternationalDbComponentConfig,
  InputMaskedTextComponentConfig,
  InputSelectComponentConfig,
  InputTextComponentConfig,
  UiComponent,
  UiComponentConfig,
  UiComponentConfigType,
  UiComponentType,
} from 'lib/interfaces/ui-components';
import { GovernmentIdSide, RequiredDevice, SelfieMatch } from 'lib/interfaces/verificationConfig';

export const generateInquiryTemplateVersionId = () => uniqueId('itmplv_');
export const generateVerificationTemplateVersionId = () => uniqueId('vtmplv_');
export const generateWorkflowVersionId = () => uniqueId('wfv_');

export const createMockFieldSchema = (): FieldSchema[] => {
  return [
    {
      key: 'name_first',
      label: 'Name First',
      type: FieldSchemaType.String,
      'default-value': 'name',
      config: { 'max-char-length': 200 },
    },
    {
      key: 'address_street_1',
      label: 'Address Street 1',
      type: FieldSchemaType.String,
      config: { 'max-char-length': 220 },
    },
    {
      key: 'email_address',
      label: 'Email Address',
      type: FieldSchemaType.String,
      config: { 'max-char-length': 255 },
    },
    {
      key: 'birth_date',
      label: 'Birth Date',
      type: FieldSchemaType.Date,
      config: {},
    },
    {
      key: 'field_key_document',
      label: 'Document Field',
      type: FieldSchemaType.DocumentGeneric,
      config: {},
    },
  ];
};

interface TransitionAttributes {
  toStep?: string;
  workflowVersionId?: string;
  workflowErrorStepName?: string;
  terminal?: boolean;
  multipleTransitions?: boolean;
  statuses?: InquiryTemplateVersionStepStatus[];
}

/**
 * Creates button component with given transition attributes
 */
export const createTransitionComponent = ({
  toStep,
  workflowVersionId,
  workflowErrorStepName,
}: TransitionAttributes = {}): ButtonSubmitComponentConfig => {
  return {
    type: UiComponentConfigType.ButtonSubmit,
    name: 'button-submit-1',
    config: {
      hidden: null,
      'hidden-server-evaluated': null,
      'button-type': ButtonType.Primary,
      'text-fallback-i18n-key': 'inquiry.ui.step_template.database.btn_submit',
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'text-translations': null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'to-step': toStep || null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'workflow-version-id': workflowVersionId || null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'workflow-on-error-step-name': workflowVersionId ? workflowErrorStepName : null,
    },
  };
};

/**
 * Generates the component preview corresponding to createTransitionComponent
 */
export const createTransitionComponentPreview = (): ButtonSubmitComponent => {
  return {
    type: UiComponentType.ButtonSubmit,
    name: 'button-submit-1',
    attributes: {
      'button-type': ButtonType.Primary,
      text: 'Continue',
      disabled: null,
      hidden: null,
    },
  };
};

export const terminalComponent: ButtonCompleteComponentConfig = {
  name: 'button-complete-1',
  type: UiComponentConfigType.ButtonComplete,
  config: {
    hidden: null,
    'hidden-server-evaluated': null,
    'button-type': ButtonType.Primary,
    'text-fallback-i18n-key': 'inquiry.ui.step_template.success.btn_submit',
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    'text-translations': null,
  },
};

export const terminalComponentPreview: ButtonCompleteComponent = {
  name: 'button-complete-1',
  type: UiComponentType.ButtonComplete,
  attributes: {
    'button-type': ButtonType.Primary,
    text: 'Done',
    disabled: null,
    hidden: null,
  },
};

/**
 * Stubs a 'blank' ui step server config with a single submit button.
 * The button is required because current add-step functionality only supports insertion between steps,
 * so a transition component must be present.
 *
 * Note: this is TEMPORARY and is intended to be replaced by a module endpoint which will
 * return the config and client_data (below) to the client, which is the pattern all other modules will follow
 */
export const createBlankUiStep = (
  { name, nameDisplay }: { name: string; nameDisplay: string },
  { toStep, workflowVersionId, workflowErrorStepName, terminal, statuses }: TransitionAttributes,
): InquiryTemplateVersionStepUi => {
  return {
    name,
    'name-display': nameDisplay,
    token: name,
    type: UiStepType.Ui,
    config: {
      'hide-cancel-button': true,
      components: [
        terminal
          ? terminalComponent
          : createTransitionComponent({
              toStep,
              workflowVersionId,
              workflowErrorStepName: workflowErrorStepName ?? name,
            }),
      ],
      statuses: terminal ? statuses || [{ name: 'completed', terminal: true, initial: false }] : [],
    },
  };
};

/**
 * Stubs a 'blank' ui step client_data with a single submit button.
 * Note: this is TEMPORARY and is intended to be replaced by a module endpoint (see above))
 */
export const createBlankUiStepPreview = (
  name: string,
  { terminal }: { terminal?: boolean } = {},
): StepUi => {
  return {
    name,
    type: UiStepType.Ui,
    config: {
      'back-step-enabled': false,
      'cancel-button-enabled': false,
      components: [terminal ? terminalComponentPreview : createTransitionComponentPreview()],
      localizations: createMockPlatformLocalizations(),
    },
  };
};

export function createMockInquiryTemplateVersion(
  attributes?: Partial<InquiryTemplateVersionAttributes>,
  relationships?: Partial<InquiryTemplateVersionRelationships>,
  id?: string,
): InquiryTemplateVersion {
  return {
    attributes: {
      'lock-version': 0,
      'created-at': '2020-02-15T09:31:58.000Z',
      'enabled-locales': ['en-US'],
      'expiration-intervals': {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'expiration-after-create-interval-seconds': null,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'expiration-after-start-interval-seconds': null,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'expiration-after-resume-interval-seconds': null,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'one-time-link-expiration-seconds': null,
      },
      'field-schema': [],
      name: 'My cool template version',
      'name-display': 'My cool display template version',
      description: 'My very cool description of this version',
      tier: InquiryTemplateVersionTier.Basic,
      'last-updater': '',
      'updated-at': '2020-02-15T09:31:58.000Z',
      'biometric-auto-migration-status': 'not_applicable',
      'biometric-consent-compliance': {
        'ignore-validation': false,
        validation: {
          'biometric-consent-text-required': false,
          'invalid-steps': [],
        },
      },
      'redirect-uri': null,
      'redirect-query-string-config': {
        'include-inquiry-id': true,
        'include-reference-id': true,
        'include-status': true,
        'include-fields': 'blocklist',
        'field-keys': [],
      },
      status: InquiryTemplateVersionStatus.Published,
      'enhanced-usages': [],
      'publish-approved': true,
      'allowed-hosts': [],
      'disable-client-create': false,
      'has-reusable-persona-config': false,
      'has-custom-reusable-persona-config': false,
      steps: [
        {
          type: UiStepType.Ui,
          name: 'start',
          'name-display': 'Start',
          token: 'base53_foobar123',
          config: {
            components: [
              {
                name: 'image-local-1',
                type: UiComponentConfigType.ImageLocal,
                config: {
                  'image-key': ImageKey.StartHero,
                  hidden: null,
                  'hidden-server-evaluated': null,
                },
                'style-variants': [
                  {
                    namespace: 'base',
                    margin: { top: null, left: null, right: null, bottom: null },
                    height: { unit: 'px', value: 165 },
                    width: { unit: 'px', value: 165 },
                    justify: StyleValueJustify.Start,
                    'fill-color': {
                      value: '',
                      unit: 'hex',
                    },
                    'fill-color--highlight': {
                      value: '',
                      unit: 'hex',
                    },
                    'fill-color--background': {
                      value: '',
                      unit: 'hex',
                    },
                    'stroke-color': {
                      value: '',
                      unit: 'hex',
                    },
                  },
                ],
              },
            ],
            statuses: [{ name: 'created', initial: true, terminal: false }],
          },
        },
      ],
      theme: { name: 'Theme' },
      ...attributes,
    },
    id: id || generateInquiryTemplateVersionId(),
    type: ApiModel.InquiryTemplateVersion,
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'inquiry-template': undefined,
      ...relationships,
    },
  };
}

/**
 * Creates mock complete step server config
 */
export function createMockCompleteStep(
  config?: Partial<InquiryTemplateVersionStepCompleteConfig>,
): InquiryTemplateVersionStepComplete {
  const nameToken = `complete-step-${uniqueId()}`;
  return {
    type: UiStepType.Complete,
    name: nameToken,
    token: nameToken,
    config: {
      statuses: [
        {
          name: 'completed',
          initial: false,
          terminal: true,
        },
        {
          name: 'approved',
          initial: false,
          terminal: true,
        },
      ],
      localizations: {
        'child-flow-page': {
          'prompt-default-value': null,
          'prompt-fallback-i18n-dynamic-variables': null,
          'prompt-fallback-i18n-key': 'inquiry.step.complete.child_flow_page.prompt',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'prompt-translations': null,
          'title-default-value': null,
          'title-fallback-i18n-dynamic-variables': null,
          'title-fallback-i18n-key': 'inquiry.step.complete.child_flow_page.title',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'title-translations': null,
        },
      },
      ...config,
    },
  };
}

/**
 * Creates mock complete step client data preview
 */
export function createMockCompleteStepPreview(
  name?: string,
  config?: Partial<StepCompleteConfig>,
): StepComplete {
  return {
    type: UiStepType.Complete,
    name: name || `complete-step-${uniqueId()}`,
    config: {
      terminal: true,
      localizations: createMockCompleteLocalizations(),
      ...config,
    },
  };
}

interface MockComponentOptions {
  withFooter?: boolean;
  terminal?: boolean;
}

function createMockDeviceHandoffConfigLocalizations(): DeviceHandoffConfigLocalizations {
  return {
    'device-handoff-prompt-page': {
      'title-default-value': null,
      'title-fallback-i18n-dynamic-variables': null,
      'title-fallback-i18n-key': null,
      'title-translations': undefined,
      'description-default-value': null,
      'description-fallback-i18n-dynamic-variables': null,
      'description-fallback-i18n-key': null,
      'description-translations': undefined,
      'qr-code-default-value': null,
      'qr-code-fallback-i18n-dynamic-variables': null,
      'qr-code-fallback-i18n-key': null,
      'qr-code-translations': undefined,
      'via-email-default-value': null,
      'via-email-fallback-i18n-dynamic-variables': null,
      'via-email-fallback-i18n-key': null,
      'via-email-translations': undefined,
      'via-sms-default-value': null,
      'via-sms-fallback-i18n-dynamic-variables': null,
      'via-sms-fallback-i18n-key': null,
      'via-sms-translations': undefined,
    },
    'device-handoff-started-page': {
      'title-default-value': null,
      'title-fallback-i18n-dynamic-variables': null,
      'title-fallback-i18n-key': null,
      'title-translations': undefined,
      'prompt-default-value': null,
      'prompt-fallback-i18n-dynamic-variables': null,
      'prompt-fallback-i18n-key': null,
      'prompt-translations': undefined,
      'btn-resume-default-value': null,
      'btn-resume-fallback-i18n-dynamic-variables': null,
      'btn-resume-fallback-i18n-key': null,
      'btn-resume-translations': undefined,
    },
    'device-handoff-email-prompt-page': {
      'title-default-value': null,
      'title-fallback-i18n-dynamic-variables': null,
      'title-fallback-i18n-key': null,
      'title-translations': undefined,
      'prompt-default-value': null,
      'prompt-fallback-i18n-dynamic-variables': null,
      'prompt-fallback-i18n-key': null,
      'prompt-translations': undefined,
      'btn-submit-default-value': null,
      'btn-submit-fallback-i18n-dynamic-variables': null,
      'btn-submit-fallback-i18n-key': null,
      'btn-submit-translations': undefined,
      'disclaimer-default-value': null,
      'disclaimer-fallback-i18n-dynamic-variables': null,
      'disclaimer-fallback-i18n-key': null,
      'disclaimer-translations': undefined,
    },
    'device-handoff-sms-prompt-page': {
      'title-default-value': null,
      'title-fallback-i18n-dynamic-variables': null,
      'title-fallback-i18n-key': null,
      'title-translations': undefined,
      'prompt-default-value': null,
      'prompt-fallback-i18n-dynamic-variables': null,
      'prompt-fallback-i18n-key': null,
      'prompt-translations': undefined,
      'btn-submit-default-value': null,
      'btn-submit-fallback-i18n-dynamic-variables': null,
      'btn-submit-fallback-i18n-key': null,
      'btn-submit-translations': undefined,
      'disclaimer-default-value': null,
      'disclaimer-fallback-i18n-dynamic-variables': null,
      'disclaimer-fallback-i18n-key': null,
      'disclaimer-translations': undefined,
    },
    'device-handoff-email-sent-page': {
      'title-default-value': null,
      'title-fallback-i18n-dynamic-variables': null,
      'title-fallback-i18n-key': null,
      'title-translations': undefined,
      'prompt-default-value': null,
      'prompt-fallback-i18n-dynamic-variables': null,
      'prompt-fallback-i18n-key': null,
      'prompt-translations': undefined,
      'btn-resend-default-value': null,
      'btn-resend-fallback-i18n-dynamic-variables': null,
      'btn-resend-fallback-i18n-key': null,
      'btn-resend-translations': undefined,
    },
    'device-handoff-sms-sent-page': {
      'title-default-value': null,
      'title-fallback-i18n-dynamic-variables': null,
      'title-fallback-i18n-key': null,
      'title-translations': undefined,
      'prompt-default-value': null,
      'prompt-fallback-i18n-dynamic-variables': null,
      'prompt-fallback-i18n-key': null,
      'prompt-translations': undefined,
      'btn-resend-default-value': null,
      'btn-resend-fallback-i18n-dynamic-variables': null,
      'btn-resend-fallback-i18n-key': null,
      'btn-resend-translations': undefined,
    },
  };
}

/**
 * Creates mock document step server config
 */
export function createMockDocumentStep(
  config?: Partial<InquiryTemplateVersionStepDocumentConfig>,
  { toStep, workflowVersionId, workflowErrorStepName }: TransitionAttributes = {},
  nameDisplay?: string,
): InquiryTemplateVersionStepDocument {
  const nameToken = ClientStepKeys.Document;
  return {
    type: UiStepType.Document,
    name: nameToken,
    'name-display': nameDisplay ?? 'Document step',
    token: nameToken,
    config: {
      ...createMockCombinedStepBaseConfig({ toStep, workflowVersionId, workflowErrorStepName }),
      'enabled-capture-options-desktop': ['web_camera', 'upload', 'device_handoff'],
      'enabled-capture-options-mobile': ['web_camera', 'mobile_camera', 'upload', 'device_handoff'],
      localizations: {
        ...createMockDeviceHandoffConfigLocalizations(),
        'capture-page': {
          'btn-additional-photo-default-value': null,
          'btn-additional-photo-fallback-i18n-dynamic-variables': null,
          'btn-additional-photo-fallback-i18n-key':
            'inquiry.step.document.capture_page.btn_additional_photo',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'btn-additional-photo-translations': null,
          'btn-finish-default-value': null,
          'btn-finish-fallback-i18n-dynamic-variables': null,
          'btn-finish-fallback-i18n-key': 'inquiry.step.document.capture_page.btn_finish',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'btn-finish-translations': null,
          'btn-take-photo-default-value': null,
          'btn-take-photo-fallback-i18n-dynamic-variables': null,
          'btn-take-photo-fallback-i18n-key': 'inquiry.step.document.capture_page.btn_take_photo',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'btn-take-photo-translations': null,
          'prompt-default-value': null,
          'prompt-fallback-i18n-dynamic-variables': null,
          'prompt-fallback-i18n-key': 'inquiry.step.document.capture_page.title',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'prompt-translations': null,
        },
        'check-page': {
          'btn-retake-default-value': null,
          'btn-retake-fallback-i18n-dynamic-variables': null,
          'btn-retake-fallback-i18n-key': 'inquiry.step.document.check_page.btn_retake',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'btn-retake-translations': null,
          'btn-submit-default-value': null,
          'btn-submit-fallback-i18n-dynamic-variables': null,
          'btn-submit-fallback-i18n-key': 'inquiry.step.document.check_page.btn_submit',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'btn-submit-translations': null,
          'confirm-upload-btn-retake-default-value': null,
          'confirm-upload-btn-retake-fallback-i18n-dynamic-variables': null,
          'confirm-upload-btn-retake-fallback-i18n-key':
            'inquiry.step.document.check_page.confirm_upload_btn_retake',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'confirm-upload-btn-retake-translations': null,
          'confirm-upload-btn-submit-default-value': null,
          'confirm-upload-btn-submit-fallback-i18n-dynamic-variables': null,
          'confirm-upload-btn-submit-fallback-i18n-key':
            'inquiry.step.document.check_page.confirm_upload_btn_submit',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'confirm-upload-btn-submit-translations': null,
          'confirm-upload-prompt-default-value': null,
          'confirm-upload-prompt-fallback-i18n-dynamic-variables': null,
          'confirm-upload-prompt-fallback-i18n-key':
            'inquiry.step.document.check_page.confirm_upload_prompt',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'confirm-upload-prompt-translations': null,
          'confirm-upload-title-default-value': null,
          'confirm-upload-title-fallback-i18n-dynamic-variables': null,
          'confirm-upload-title-fallback-i18n-key':
            'inquiry.step.document.check_page.confirm_upload_title',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'confirm-upload-title-translations': null,
          'confirm-upload-uploading-default-value': null,
          'confirm-upload-uploading-fallback-i18n-dynamic-variables': null,
          'confirm-upload-uploading-fallback-i18n-key':
            'inquiry.step.document.check_page.confirm_upload_uploading',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'confirm-upload-uploading-translations': null,
          'large-file-upload-default-value': null,
          'large-file-upload-fallback-i18n-dynamic-variables': null,
          'large-file-upload-fallback-i18n-key':
            'inquiry.step.document.check_page.large_file_upload',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'large-file-upload-translations': null,
          'large-image-upload-default-value': null,
          'large-image-upload-fallback-i18n-dynamic-variables': null,
          'large-image-upload-fallback-i18n-key':
            'inquiry.step.document.check_page.large_image_upload',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'large-image-upload-translations': null,
          'prompt-default-value': null,
          'prompt-fallback-i18n-dynamic-variables': null,
          'prompt-fallback-i18n-key': 'inquiry.step.document.check_page.prompt',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'prompt-translations': null,
          'title-default-value': null,
          'title-fallback-i18n-dynamic-variables': null,
          'title-fallback-i18n-key': 'inquiry.step.document.check_page.title',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'title-translations': null,
          'uploading-default-value': null,
          'uploading-fallback-i18n-dynamic-variables': null,
          'uploading-fallback-i18n-key': 'inquiry.step.document.check_page.uploading',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'uploading-translations': null,
        },
        'pending-page': {
          'description-default-value': null,
          'description-fallback-i18n-dynamic-variables': null,
          'description-fallback-i18n-key': 'inquiry.step.document.pending_page.description',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'description-translations': null,
          'title-default-value': null,
          'title-fallback-i18n-dynamic-variables': null,
          'title-fallback-i18n-key': 'inquiry.step.document.pending_page.title',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'title-translations': null,
        },
        'prompt-page': {
          'btn-cancel-default-value': null,
          'btn-cancel-fallback-i18n-dynamic-variables': null,
          'btn-cancel-fallback-i18n-key': 'inquiry.step.document.prompt_page.btn_cancel',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'btn-cancel-translations': null,
          'btn-capture-default-value': null,
          'btn-capture-fallback-i18n-dynamic-variables': null,
          'btn-capture-fallback-i18n-key': 'inquiry.step.document.prompt_page.btn_capture',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'btn-capture-translations': null,
          'btn-delete-default-value': null,
          'btn-delete-fallback-i18n-dynamic-variables': null,
          'btn-delete-fallback-i18n-key': 'inquiry.step.document.prompt_page.btn_delete',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'btn-delete-translations': null,
          'btn-device-handoff-default-value': null,
          'btn-device-handoff-fallback-i18n-dynamic-variables': null,
          'btn-device-handoff-fallback-i18n-key':
            'inquiry.step.document.prompt_page.btn_device_handoff',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'btn-device-handoff-translations': null,
          'btn-submit-default-value': null,
          'btn-submit-fallback-i18n-dynamic-variables': null,
          'btn-submit-fallback-i18n-key': 'inquiry.step.document.prompt_page.btn_submit',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'btn-submit-translations': null,
          'btn-upload-default-value': null,
          'btn-upload-fallback-i18n-dynamic-variables': null,
          'btn-upload-fallback-i18n-key': 'inquiry.step.document.prompt_page.btn_upload',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'btn-upload-translations': null,
          'camera-permissions-btn-cancel-default-value': null,
          'camera-permissions-btn-cancel-fallback-i18n-dynamic-variables': null,
          'camera-permissions-btn-cancel-fallback-i18n-key':
            'inquiry.step.document.prompt_page.camera_permissions_btn_cancel',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'camera-permissions-btn-cancel-translations': null,
          'camera-permissions-btn-continue-mobile-default-value': null,
          'camera-permissions-btn-continue-mobile-fallback-i18n-dynamic-variables': null,
          'camera-permissions-btn-continue-mobile-fallback-i18n-key':
            'inquiry.step.document.prompt_page.camera_permissions_btn_continue_mobile',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'camera-permissions-btn-continue-mobile-translations': null,
          'camera-permissions-btn-continue-web-default-value': null,
          'camera-permissions-btn-continue-web-fallback-i18n-dynamic-variables': null,
          'camera-permissions-btn-continue-web-fallback-i18n-key':
            'inquiry.step.document.prompt_page.camera_permissions_btn_continue_web',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'camera-permissions-btn-continue-web-translations': null,
          'camera-permissions-prompt-default-value': null,
          'camera-permissions-prompt-fallback-i18n-dynamic-variables': null,
          'camera-permissions-prompt-fallback-i18n-key':
            'inquiry.step.document.prompt_page.camera_permissions_prompt',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'camera-permissions-prompt-translations': null,
          'camera-permissions-title-default-value': null,
          'camera-permissions-title-fallback-i18n-dynamic-variables': null,
          'camera-permissions-title-fallback-i18n-key':
            'inquiry.step.document.prompt_page.camera_permissions_title',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'camera-permissions-title-translations': null,
          'capture-options-dialog-title-default-value': null,
          'capture-options-dialog-title-fallback-i18n-dynamic-variables': null,
          'capture-options-dialog-title-fallback-i18n-key':
            'inquiry.step.document.prompt_page.capture_options_dialog_title',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'capture-options-dialog-title-translations': null,
          'photos-permissions-body-default-value': null,
          'photos-permissions-body-fallback-i18n-dynamic-variables': null,
          'photos-permissions-body-fallback-i18n-key':
            'inquiry.step.document.prompt_page.photos_permissions_body',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'photos-permissions-body-translations': null,
          'photos-permissions-dismiss-button-text-default-value': null,
          'photos-permissions-dismiss-button-text-fallback-i18n-dynamic-variables': null,
          'photos-permissions-dismiss-button-text-fallback-i18n-key':
            'inquiry.step.document.prompt_page.photos_permissions_dismiss_button_text',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'photos-permissions-dismiss-button-text-translations': null,
          'photos-permissions-enable-button-text-default-value': null,
          'photos-permissions-enable-button-text-fallback-i18n-dynamic-variables': null,
          'photos-permissions-enable-button-text-fallback-i18n-key':
            'inquiry.step.document.prompt_page.photos_permissions_enable_button_text',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'photos-permissions-enable-button-text-translations': null,
          'photos-permissions-settings-button-text-default-value': null,
          'photos-permissions-settings-button-text-fallback-i18n-dynamic-variables': null,
          'photos-permissions-settings-button-text-fallback-i18n-key':
            'inquiry.step.document.prompt_page.photos_permissions_settings_button_text',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'photos-permissions-settings-button-text-translations': null,
          'photos-permissions-settings-text-default-value': null,
          'photos-permissions-settings-text-fallback-i18n-dynamic-variables': null,
          'photos-permissions-settings-text-fallback-i18n-key':
            'inquiry.step.document.prompt_page.photos_permissions_settings_text',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'photos-permissions-settings-text-translations': null,
          'photos-permissions-title-default-value': null,
          'photos-permissions-title-fallback-i18n-dynamic-variables': null,
          'photos-permissions-title-fallback-i18n-key':
            'inquiry.step.document.prompt_page.photos_permissions_title',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'photos-permissions-title-translations': null,
          'delete-dialog-title-default-value': null,
          'delete-dialog-title-fallback-i18n-dynamic-variables': null,
          'delete-dialog-title-fallback-i18n-key':
            'inquiry.step.document.prompt_page.delete_dialog_title',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'delete-dialog-title-translations': null,
          'no-capture-options-prompt-default-value': null,
          'no-capture-options-prompt-fallback-i18n-dynamic-variables': null,
          'no-capture-options-prompt-fallback-i18n-key':
            'inquiry.step.document.prompt_page.no_capture_options_prompt',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'no-capture-options-prompt-translations': null,
          'prompt-default-value': null,
          'prompt-fallback-i18n-dynamic-variables': null,
          'prompt-fallback-i18n-key': 'inquiry.step.document.prompt_page.prompt',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'prompt-translations': null,
          'title-default-value': null,
          'title-fallback-i18n-dynamic-variables': null,
          'title-fallback-i18n-key': 'inquiry.step.document.prompt_page.title',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'title-translations': null,
        },
      },
      ...config,
    },
  };
}

/**
 * Creates mock goverment ID step server config
 */
export function createMockGovernmentIdStep(
  config?: Partial<InquiryTemplateVersionStepGovernmentIdConfig>,
  { toStep, workflowVersionId, workflowErrorStepName }: TransitionAttributes = {},
  nameDisplay?: string,
): InquiryTemplateVersionStepGovernmentId {
  return {
    type: UiStepType.GovernmentId,
    name: ClientStepKeys.GovernmentId,
    'name-display': nameDisplay ?? 'Government ID step',
    token: ClientStepKeys.GovernmentId,
    config: {
      ...createMockCombinedStepBaseConfig({ toStep, workflowVersionId, workflowErrorStepName }),
      disclaimer: 'default',
      'enabled-skip-unknown': false,
      'field-key-country': 'field-key-country',
      'field-key-document': 'field-key-document',
      'field-key-idclass': 'field-key-id-class',
      idclasses: [
        {
          class: 'dl',
          'requires-device': RequiredDevice.None,
          'requires-sides': [GovernmentIdSide.Front],
        },
        {
          class: 'cid',
          'requires-device': RequiredDevice.None,
          'requires-sides': [GovernmentIdSide.Front],
        },
      ],
      'govid-design-version': GOVERNMENT_ID_DESIGN_VERSION.V1,
      'enabled-capture-options-desktop': ['web_camera', 'upload', 'device_handoff'],
      'enabled-capture-options-mobile': ['web_camera', 'mobile_camera', 'upload', 'device_handoff'],
      'enabled-capture-options-native-mobile': ['mobile_camera', 'upload'],
      'always-show-mobile-camera-capture-option': false,
      localizations: {
        ...createMockDeviceHandoffConfigLocalizations(),
        'capture-page': {
          'btn-take-photo-default-value': null,
          'btn-take-photo-fallback-i18n-dynamic-variables': null,
          'btn-take-photo-fallback-i18n-key': 'inquiry.step.document.capture_page.btn_take_photo',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'btn-take-photo-translations': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'disclaimer-translations': null,
          'disclaimer-fallback-i18n-key': 'inquiry.step.government_id.capture_page.disclaimer',
          'disclaimer-fallback-i18n-dynamic-variables': null,
          'disclaimer-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'scan-front-translations': null,
          'scan-front-fallback-i18n-key': 'inquiry.step.government_id.capture_page.scan_front',
          'scan-front-fallback-i18n-dynamic-variables': null,
          'scan-front-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'scan-back-translations': null,
          'scan-back-fallback-i18n-key': 'inquiry.step.government_id.capture_page.scan_back',
          'scan-back-fallback-i18n-dynamic-variables': null,
          'scan-back-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'scan-pdf417-translations': null,
          'scan-pdf417-fallback-i18n-key': 'inquiry.step.government_id.capture_page.scan_pdf417',
          'scan-pdf417-fallback-i18n-dynamic-variables': null,
          'scan-pdf417-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'scan-mrz-translations': null,
          'scan-mrz-fallback-i18n-key': 'inquiry.step.government_id.capture_page.scan_mrz',
          'scan-mrz-fallback-i18n-dynamic-variables': null,
          'scan-mrz-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'scan-front-or-back-translations': null,
          'scan-front-or-back-fallback-i18n-key':
            'inquiry.step.government_id.capture_page.scan_front_or_back',
          'scan-front-or-back-fallback-i18n-dynamic-variables': null,
          'scan-front-or-back-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'scan-signature-translations': null,
          'scan-signature-fallback-i18n-key':
            'inquiry.step.government_id.capture_page.scan_signature',
          'scan-signature-fallback-i18n-dynamic-variables': null,
          'scan-signature-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'capturing-translations': null,
          'capturing-fallback-i18n-key': 'inquiry.step.government_id.capture_page.capturing',
          'capturing-fallback-i18n-dynamic-variables': null,
          'capturing-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'confirm-capture-translations': null,
          'confirm-capture-fallback-i18n-key':
            'inquiry.step.government_id.capture_page.confirm_capture',
          'confirm-capture-fallback-i18n-dynamic-variables': null,
          'confirm-capture-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'prompt-override-translations': null,
          'prompt-override-default-value': '',
          'prompt-override-fallback-i18n-key': null,
          'prompt-override-fallback-i18n-dynamic-variables': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'title-translations': null,
          'title-default-value': '',
          'title-fallback-i18n-key': null,
          'title-fallback-i18n-dynamic-variables': null,
        },
        'check-page': {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'title-translations': null,
          'title-fallback-i18n-key': 'inquiry.step.government_id.check_page.title',
          'title-fallback-i18n-dynamic-variables': null,
          'title-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'title-confirm-capture-translations': null,
          'title-confirm-capture-default-value': '',
          'title-confirm-capture-fallback-i18n-key': null,
          'title-confirm-capture-fallback-i18n-dynamic-variables': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'prompt-translations': null,
          'prompt-fallback-i18n-key': 'inquiry.step.government_id.check_page.prompt',
          'prompt-fallback-i18n-dynamic-variables': null,
          'prompt-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'uploading-translations': null,
          'uploading-fallback-i18n-key': 'inquiry.step.government_id.check_page.uploading',
          'uploading-fallback-i18n-dynamic-variables': null,
          'uploading-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'large-upload-translations': null,
          'large-upload-fallback-i18n-key': 'inquiry.step.government_id.check_page.large_upload',
          'large-upload-fallback-i18n-dynamic-variables': null,
          'large-upload-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'btn-submit-translations': null,
          'btn-submit-fallback-i18n-key': 'inquiry.step.government_id.check_page.btn_submit',
          'btn-submit-fallback-i18n-dynamic-variables': null,
          'btn-submit-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'btn-retake-translations': null,
          'btn-retake-fallback-i18n-key': 'inquiry.step.government_id.check_page.btn_retake',
          'btn-retake-fallback-i18n-dynamic-variables': null,
          'btn-retake-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'confirm-title-translations': null,
          'confirm-title-fallback-i18n-key': 'inquiry.step.government_id.check_page.confirm_title',
          'confirm-title-fallback-i18n-dynamic-variables': null,
          'confirm-title-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'confirm-prompt-translations': null,
          'confirm-prompt-fallback-i18n-key':
            'inquiry.step.government_id.check_page.confirm_prompt',
          'confirm-prompt-fallback-i18n-dynamic-variables': null,
          'confirm-prompt-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'confirm-uploading-translations': null,
          'confirm-uploading-fallback-i18n-key':
            'inquiry.step.government_id.check_page.confirm_uploading',
          'confirm-uploading-fallback-i18n-dynamic-variables': null,
          'confirm-uploading-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'confirm-btn-submit-translations': null,
          'confirm-btn-submit-fallback-i18n-key':
            'inquiry.step.government_id.check_page.confirm_btn_submit',
          'confirm-btn-submit-fallback-i18n-dynamic-variables': null,
          'confirm-btn-submit-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'confirm-btn-retake-translations': null,
          'confirm-btn-retake-fallback-i18n-key':
            'inquiry.step.government_id.check_page.confirm_btn_retake',
          'confirm-btn-retake-fallback-i18n-dynamic-variables': null,
          'confirm-btn-retake-default-value': null,
        },
        'pending-page': {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'title-translations': null,
          'title-fallback-i18n-key': 'inquiry.step.government_id.pending_page.title',
          'title-fallback-i18n-dynamic-variables': null,
          'title-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'description-translations': null,
          'description-fallback-i18n-key': 'inquiry.step.government_id.pending_page.description',
          'description-fallback-i18n-dynamic-variables': null,
          'description-default-value': null,
        },
        'prompt-page': {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'btn-capture-translations': null,
          'btn-capture-fallback-i18n-key': 'inquiry.step.government_id.prompt_page.btn_capture',
          'btn-capture-fallback-i18n-dynamic-variables': null,
          'btn-capture-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'btn-capture-mobile-camera-translations': null,
          'btn-capture-mobile-camera-fallback-i18n-key': null,
          'btn-capture-mobile-camera-fallback-i18n-dynamic-variables': null,
          'btn-capture-mobile-camera-default-value': '',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'btn-device-handoff-translations': null,
          'btn-device-handoff-fallback-i18n-key':
            'inquiry.step.government_id.prompt_page.btn_device_handoff',
          'btn-device-handoff-fallback-i18n-dynamic-variables': null,
          'btn-device-handoff-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'btn-upload-translations': null,
          'btn-upload-fallback-i18n-key': 'inquiry.step.government_id.prompt_page.btn_upload',
          'btn-upload-fallback-i18n-dynamic-variables': null,
          'btn-upload-default-value': null,
          'camera-permissions-btn-cancel-default-value': null,
          'camera-permissions-btn-cancel-fallback-i18n-dynamic-variables': null,
          'camera-permissions-btn-cancel-fallback-i18n-key':
            'inquiry.step.government_id.prompt_page.camera_permissions_btn_cancel',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'camera-permissions-btn-cancel-translations': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'camera-permissions-btn-continue-mobile-translations': null,
          'camera-permissions-btn-continue-mobile-fallback-i18n-key':
            'inquiry.step.government_id.prompt_page.camera_permissions_btn_continue_mobile',
          'camera-permissions-btn-continue-mobile-fallback-i18n-dynamic-variables': null,
          'camera-permissions-btn-continue-mobile-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'camera-permissions-btn-continue-web-translations': null,
          'camera-permissions-btn-continue-web-fallback-i18n-key':
            'inquiry.step.government_id.prompt_page.camera_permissions_btn_continue_web',
          'camera-permissions-btn-continue-web-fallback-i18n-dynamic-variables': null,
          'camera-permissions-btn-continue-web-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'camera-permissions-prompt-translations': null,
          'camera-permissions-prompt-fallback-i18n-key':
            'inquiry.step.government_id.prompt_page.camera_permissions_prompt',
          'camera-permissions-prompt-fallback-i18n-dynamic-variables': null,
          'camera-permissions-prompt-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'camera-permissions-title-translations': null,
          'camera-permissions-title-fallback-i18n-key':
            'inquiry.step.government_id.prompt_page.camera_permissions_title',
          'camera-permissions-title-fallback-i18n-dynamic-variables': null,
          'camera-permissions-title-default-value': null,
          'photos-permissions-body-default-value': null,
          'photos-permissions-body-fallback-i18n-dynamic-variables': null,
          'photos-permissions-body-fallback-i18n-key':
            'inquiry.step.government_id.prompt_page.photos_permissions_body',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'photos-permissions-body-translations': null,
          'photos-permissions-dismiss-button-text-default-value': null,
          'photos-permissions-dismiss-button-text-fallback-i18n-dynamic-variables': null,
          'photos-permissions-dismiss-button-text-fallback-i18n-key':
            'inquiry.step.government_id.prompt_page.photos_permissions_dismiss_button_text',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'photos-permissions-dismiss-button-text-translations': null,
          'photos-permissions-enable-button-text-default-value': null,
          'photos-permissions-enable-button-text-fallback-i18n-dynamic-variables': null,
          'photos-permissions-enable-button-text-fallback-i18n-key':
            'inquiry.step.government_id.prompt_page.photos_permissions_enable_button_text',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'photos-permissions-enable-button-text-translations': null,
          'photos-permissions-settings-button-text-default-value': null,
          'photos-permissions-settings-button-text-fallback-i18n-dynamic-variables': null,
          'photos-permissions-settings-button-text-fallback-i18n-key':
            'inquiry.step.government_id.prompt_page.photos_permissions_settings_button_text',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'photos-permissions-settings-button-text-translations': null,
          'photos-permissions-settings-text-default-value': null,
          'photos-permissions-settings-text-fallback-i18n-dynamic-variables': null,
          'photos-permissions-settings-text-fallback-i18n-key':
            'inquiry.step.government_id.prompt_page.photos_permissions_settings_text',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'photos-permissions-settings-text-translations': null,
          'photos-permissions-title-default-value': null,
          'photos-permissions-title-fallback-i18n-dynamic-variables': null,
          'photos-permissions-title-fallback-i18n-key':
            'inquiry.step.government_id.prompt_page.photos_permissions_title',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'photos-permissions-title-translations': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'no-capture-options-prompt-translations': null,
          'no-capture-options-prompt-fallback-i18n-key':
            'inquiry.step.government_id.prompt_page.no_capture_options_prompt',
          'no-capture-options-prompt-fallback-i18n-dynamic-variables': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'no-capture-options-prompt-default-value': null,
        },
        'select-page': {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'disclaimer-translations': null,
          'disclaimer-fallback-i18n-key': 'inquiry.step.government_id.select_page.disclaimer',
          'disclaimer-fallback-i18n-dynamic-variables': null,
          'disclaimer-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'option-notice-translations': null,
          'option-notice-fallback-i18n-key': 'inquiry.step.government_id.select_page.option_notice',
          'option-notice-fallback-i18n-dynamic-variables': null,
          'option-notice-default-value': null,
          'prompt-translations': [],
          'prompt-fallback-i18n-key': 'inquiry.step.government_id.select_page.prompt',
          'prompt-fallback-i18n-dynamic-variables': null,
          'prompt-default-value': null,
          'title-translations': [],
          'title-fallback-i18n-key': 'inquiry.step.government_id.select_page.title',
          'title-fallback-i18n-dynamic-variables': null,
          'title-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'unsupported-title-translations': null,
          'unsupported-title-fallback-i18n-key':
            'inquiry.step.government_id.select_page.unsupported_title',
          'unsupported-title-fallback-i18n-dynamic-variables': null,
          'unsupported-title-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'unsupported-prompt-translations': null,
          'unsupported-prompt-fallback-i18n-key':
            'inquiry.step.government_id.select_page.unsupported_prompt',
          'unsupported-prompt-fallback-i18n-dynamic-variables': null,
          'unsupported-prompt-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'cct-translations': null,
          'cct-fallback-i18n-key': 'inquiry.step.government_id.select_page.cct',
          'cct-fallback-i18n-dynamic-variables': null,
          'cct-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'cid-translations': null,
          'cid-fallback-i18n-key': 'inquiry.step.government_id.select_page.cid',
          'cid-fallback-i18n-dynamic-variables': null,
          'cid-default-value': null,
          'dl-translations': [],
          'dl-fallback-i18n-key': 'inquiry.step.government_id.select_page.dl',
          'dl-fallback-i18n-dynamic-variables': null,
          'dl-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'foid-translations': null,
          'foid-fallback-i18n-key': 'inquiry.step.government_id.select_page.foid',
          'foid-fallback-i18n-dynamic-variables': null,
          'foid-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'hic-translations': null,
          'hic-fallback-i18n-key': 'inquiry.step.government_id.select_page.hic',
          'hic-fallback-i18n-dynamic-variables': null,
          'hic-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'id-translations': null,
          'id-fallback-i18n-key': 'inquiry.step.government_id.select_page.id',
          'id-fallback-i18n-dynamic-variables': null,
          'id-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'ipp-translations': null,
          'ipp-fallback-i18n-key': 'inquiry.step.government_id.select_page.ipp',
          'ipp-fallback-i18n-dynamic-variables': null,
          'ipp-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'keyp-translations': null,
          'keyp-fallback-i18n-key': 'inquiry.step.government_id.select_page.keyp',
          'keyp-fallback-i18n-dynamic-variables': null,
          'keyp-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'ltpass-translations': null,
          'ltpass-fallback-i18n-key': 'inquiry.step.government_id.select_page.ltpass',
          'ltpass-fallback-i18n-dynamic-variables': null,
          'ltpass-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'mid-translations': null,
          'mid-fallback-i18n-key': 'inquiry.step.government_id.select_page.mid',
          'mid-fallback-i18n-dynamic-variables': null,
          'mid-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'munid-translations': null,
          'munid-fallback-i18n-key': 'inquiry.step.government_id.select_page.munid',
          'munid-fallback-i18n-dynamic-variables': null,
          'munid-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'myn-translations': null,
          'myn-fallback-i18n-key': 'inquiry.step.government_id.select_page.myn',
          'myn-fallback-i18n-dynamic-variables': null,
          'myn-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'nbi-translations': null,
          'nbi-fallback-i18n-key': 'inquiry.step.government_id.select_page.nbi',
          'nbi-fallback-i18n-dynamic-variables': null,
          'nbi-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'nric-translations': null,
          'nric-fallback-i18n-key': 'inquiry.step.government_id.select_page.nric',
          'nric-fallback-i18n-dynamic-variables': null,
          'nric-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'ofw-translations': null,
          'ofw-fallback-i18n-key': 'inquiry.step.government_id.select_page.ofw',
          'ofw-fallback-i18n-dynamic-variables': null,
          'ofw-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'pan-translations': null,
          'pan-fallback-i18n-key': 'inquiry.step.government_id.select_page.pan',
          'pan-fallback-i18n-dynamic-variables': null,
          'pan-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'pid-translations': null,
          'pid-fallback-i18n-key': 'inquiry.step.government_id.select_page.pid',
          'pid-fallback-i18n-dynamic-variables': null,
          'pid-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'pp-translations': null,
          'pp-fallback-i18n-key': 'inquiry.step.government_id.select_page.pp',
          'pp-fallback-i18n-dynamic-variables': null,
          'pp-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'ppc-translations': null,
          'ppc-fallback-i18n-key': 'inquiry.step.government_id.select_page.ppc',
          'ppc-fallback-i18n-dynamic-variables': null,
          'ppc-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'pr-translations': null,
          'pr-fallback-i18n-key': 'inquiry.step.government_id.select_page.pr',
          'pr-fallback-i18n-dynamic-variables': null,
          'pr-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'rp-translations': null,
          'rp-fallback-i18n-key': 'inquiry.step.government_id.select_page.rp',
          'rp-fallback-i18n-dynamic-variables': null,
          'rp-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'sss-translations': null,
          'sss-fallback-i18n-key': 'inquiry.step.government_id.select_page.sss',
          'sss-fallback-i18n-dynamic-variables': null,
          'sss-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'td-translations': null,
          'td-fallback-i18n-key': 'inquiry.step.government_id.select_page.td',
          'td-fallback-i18n-dynamic-variables': null,
          'td-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'tribalid-translations': null,
          'tribalid-fallback-i18n-key': 'inquiry.step.government_id.select_page.tribalid',
          'tribalid-fallback-i18n-dynamic-variables': null,
          'tribalid-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'umid-translations': null,
          'umid-fallback-i18n-key': 'inquiry.step.government_id.select_page.umid',
          'umid-fallback-i18n-dynamic-variables': null,
          'umid-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'unknown-translations': null,
          'unknown-fallback-i18n-key': 'inquiry.step.government_id.select_page.unknown',
          'unknown-fallback-i18n-dynamic-variables': null,
          'unknown-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'us-id-translations': null,
          'us-id-fallback-i18n-key': 'inquiry.step.government_id.select_page.us_id',
          'us-id-fallback-i18n-dynamic-variables': null,
          'us-id-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'vid-translations': null,
          'vid-fallback-i18n-key': 'inquiry.step.government_id.select_page.vid',
          'vid-fallback-i18n-dynamic-variables': null,
          'vid-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'visa-translations': null,
          'visa-fallback-i18n-key': 'inquiry.step.government_id.select_page.visa',
          'visa-fallback-i18n-dynamic-variables': null,
          'visa-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'wp-translations': null,
          'wp-fallback-i18n-key': 'inquiry.step.government_id.select_page.wp',
          'wp-fallback-i18n-dynamic-variables': null,
          'wp-default-value': null,
        },
        'request-page': {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'choose-photo-button-text-translations': null,
          'choose-photo-button-text-fallback-i18n-key':
            'inquiry.step.government_id.request_page.choose_photo_button_text',
          'choose-photo-button-text-fallback-i18n-dynamic-variables': null,
          'choose-photo-button-text-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'description-back-translations': null,
          'description-back-fallback-i18n-key':
            'inquiry.step.government_id.request_page.description_back',
          'description-back-fallback-i18n-dynamic-variables': null,
          'description-back-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'description-front-translations': null,
          'description-front-fallback-i18n-key':
            'inquiry.step.government_id.request_page.description_front',
          'description-front-fallback-i18n-dynamic-variables': null,
          'description-front-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'description-pdf417-translations': null,
          'description-pdf417-fallback-i18n-key':
            'inquiry.step.government_id.request_page.description_pdf417',
          'description-pdf417-fallback-i18n-dynamic-variables': null,
          'description-pdf417-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'description-passport-signature-translations': null,
          'description-passport-signature-fallback-i18n-key':
            'inquiry.step.government_id.request_page.description_passport_signature',
          'description-passport-signature-fallback-i18n-dynamic-variables': null,
          'description-passport-signature-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'live-upload-button-text-translations': null,
          'live-upload-button-text-fallback-i18n-key':
            'inquiry.step.government_id.request_page.live_upload_button_text',
          'live-upload-button-text-fallback-i18n-dynamic-variables': null,
          'live-upload-button-text-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'title-back-translations': null,
          'title-back-fallback-i18n-key': 'inquiry.step.government_id.request_page.title_back',
          'title-back-fallback-i18n-dynamic-variables': null,
          'title-back-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'title-front-translations': null,
          'title-front-fallback-i18n-key': 'inquiry.step.government_id.request_page.title_front',
          'title-front-fallback-i18n-dynamic-variables': null,
          'title-front-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'title-pdf417-translations': null,
          'title-pdf417-fallback-i18n-key': 'inquiry.step.government_id.request_page.title_pdf417',
          'title-pdf417-fallback-i18n-dynamic-variables': null,
          'title-pdf417-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'title-passport-signature-translations': null,
          'title-passport-signature-fallback-i18n-key':
            'inquiry.step.government_id.request_page.title_passport_signature',
          'title-passport-signature-fallback-i18n-dynamic-variables': null,
          'title-passport-signature-default-value': null,
        },
        'review-upload-page': {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'choose-another-button-text-translations': null,
          'choose-another-button-text-fallback-i18n-key':
            'inquiry.step.government_id.review_page.choose_another_button_text',
          'choose-another-button-text-fallback-i18n-dynamic-variables': null,
          'choose-another-button-text-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'confirm-button-text-translations': null,
          'confirm-button-text-fallback-i18n-key':
            'inquiry.step.government_id.review_page.confirm_button_text',
          'confirm-button-text-fallback-i18n-dynamic-variables': null,
          'confirm-button-text-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'description-back-translations': null,
          'description-back-fallback-i18n-key':
            'inquiry.step.government_id.review_page.description_back',
          'description-back-fallback-i18n-dynamic-variables': null,
          'description-back-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'description-front-translations': null,
          'description-front-fallback-i18n-key':
            'inquiry.step.government_id.review_page.description_front',
          'description-front-fallback-i18n-dynamic-variables': null,
          'description-front-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'description-pdf417-translations': null,
          'description-pdf417-fallback-i18n-key':
            'inquiry.step.government_id.review_page.description_pdf417',
          'description-pdf417-fallback-i18n-dynamic-variables': null,
          'description-pdf417-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'description-passport-signature-translations': null,
          'description-passport-signature-fallback-i18n-key':
            'inquiry.step.government_id.review_page.description_passport_signature',
          'description-passport-signature-fallback-i18n-dynamic-variables': null,
          'description-passport-signature-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'title-back-translations': null,
          'title-back-fallback-i18n-key': 'inquiry.step.government_id.review_page.title_back',
          'title-back-fallback-i18n-dynamic-variables': null,
          'title-back-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'title-front-translations': null,
          'title-front-fallback-i18n-key': 'inquiry.step.government_id.review_page.title_front',
          'title-front-fallback-i18n-dynamic-variables': null,
          'title-front-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'title-pdf417-translations': null,
          'title-pdf417-fallback-i18n-key': 'inquiry.step.government_id.review_page.title_pdf417',
          'title-pdf417-fallback-i18n-dynamic-variables': null,
          'title-pdf417-default-value': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'title-passport-signature-translations': null,
          'title-passport-signature-fallback-i18n-key':
            'inquiry.step.government_id.review_page.title_passport_signature',
          'title-passport-signature-fallback-i18n-dynamic-variables': null,
          'title-passport-signature-default-value': null,
        },
      },
      ...config,
    },
  };
}

/**
 * Creates mock selfie step server config
 */
export function createMockSelfieStep(
  config?: Partial<InquiryTemplateVersionStepSelfieConfig>,
  { toStep, workflowVersionId, workflowErrorStepName }: TransitionAttributes = {},
  nameDisplay?: string,
): InquiryTemplateVersionStepSelfie {
  const nameToken = ClientStepKeys.Selfie;
  return {
    type: UiStepType.Selfie,
    name: nameToken,
    'name-display': nameDisplay ?? 'Selfie step',
    token: nameToken,
    config: {
      ...createMockCombinedStepBaseConfig({ toStep, workflowVersionId, workflowErrorStepName }),
      'selfie-type': SelfieMatch.ProfileAndCenter,
      'field-key-selfie': 'field-key-selfie',
      'web-camera-allow-stillfie-fallback': false,
      'enabled-capture-options-desktop': ['web_camera', 'device_handoff'],
      'enabled-capture-options-mobile': ['web_camera', 'device_handoff'],
      localizations: {
        ...createMockDeviceHandoffConfigLocalizations(),
        'capture-page': {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'title-translations': null,
          'title-default-value': null,
          'title-fallback-i18n-key': null,
          'title-fallback-i18n-dynamic-variables': null,
          'selfie-hint-center-delayed-default-value': null,
          'selfie-hint-center-delayed-fallback-i18n-dynamic-variables': null,
          'selfie-hint-center-delayed-fallback-i18n-key': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'selfie-hint-center-delayed-translations': null,
          'selfie-hint-center-face-default-value': null,
          'selfie-hint-center-face-fallback-i18n-dynamic-variables': null,
          'selfie-hint-center-face-fallback-i18n-key': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'selfie-hint-center-face-translations': null,
          'selfie-hint-hold-still-default-value': null,
          'selfie-hint-hold-still-fallback-i18n-dynamic-variables': null,
          'selfie-hint-hold-still-fallback-i18n-key': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'selfie-hint-hold-still-translations': null,
          'selfie-hint-look-left-default-value': null,
          'selfie-hint-look-left-fallback-i18n-dynamic-variables': null,
          'selfie-hint-look-left-fallback-i18n-key': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'selfie-hint-look-left-translations': null,
          'selfie-hint-look-right-default-value': null,
          'selfie-hint-look-right-fallback-i18n-dynamic-variables': null,
          'selfie-hint-look-right-fallback-i18n-key': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'selfie-hint-look-right-translations': null,
          'selfie-hint-face-too-close-default-value': null,
          'selfie-hint-face-too-close-fallback-i18n-dynamic-variables': null,
          'selfie-hint-face-too-close-fallback-i18n-key': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'selfie-hint-face-too-close-translations': null,
          'selfie-hint-face-too-far-default-value': null,
          'selfie-hint-face-too-far-fallback-i18n-dynamic-variables': null,
          'selfie-hint-face-too-far-fallback-i18n-key': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'selfie-hint-face-too-far-translations': null,
          'selfie-hint-multiple-faces-default-value': null,
          'selfie-hint-multiple-faces-fallback-i18n-dynamic-variables': null,
          'selfie-hint-multiple-faces-fallback-i18n-key': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'selfie-hint-multiple-faces-translations': null,
          'selfie-hint-pose-not-center-default-value': null,
          'selfie-hint-pose-not-center-fallback-i18n-dynamic-variables': null,
          'selfie-hint-pose-not-center-fallback-i18n-key': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'selfie-hint-pose-not-center-translations': null,
          'selfie-hint-take-photo-default-value': null,
          'selfie-hint-take-photo-fallback-i18n-dynamic-variables': null,
          'selfie-hint-take-photo-fallback-i18n-key': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'selfie-hint-take-photo-translations': null,
          'selfie-hint-verifying-default-value': null,
          'selfie-hint-verifying-fallback-i18n-dynamic-variables': null,
          'selfie-hint-verifying-fallback-i18n-key': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'selfie-hint-verifying-translations': null,
        },
        'pending-page': {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'title-translations': null,
          'title-default-value': null,
          'title-fallback-i18n-key': null,
          'title-fallback-i18n-dynamic-variables': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'description-translations': null,
          'description-default-value': null,
          'description-fallback-i18n-key': null,
          'description-fallback-i18n-dynamic-variables': null,
        },
        'prompt-page': {
          'agree-to-policy-default-value': null,
          'agree-to-policy-fallback-i18n-dynamic-variables': null,
          'agree-to-policy-fallback-i18n-key': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'agree-to-policy-translations': null,
          'btn-device-handoff-default-value': null,
          'btn-device-handoff-fallback-i18n-dynamic-variables': null,
          'btn-device-handoff-fallback-i18n-key': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'btn-device-handoff-translations': null,
          'btn-submit-default-value': null,
          'btn-submit-fallback-i18n-dynamic-variables': null,
          'btn-submit-fallback-i18n-key': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'btn-submit-translations': null,
          'no-capture-options-prompt-default-value': null,
          'no-capture-options-prompt-fallback-i18n-dynamic-variables': null,
          'no-capture-options-prompt-fallback-i18n-key': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'no-capture-options-prompt-translations': null,
          'selfie-prompt-center-default-value': null,
          'selfie-prompt-center-fallback-i18n-dynamic-variables': null,
          'selfie-prompt-center-fallback-i18n-key': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'selfie-prompt-center-translations': null,
          'selfie-prompt-default-value': null,
          'selfie-prompt-fallback-i18n-dynamic-variables': null,
          'selfie-prompt-fallback-i18n-key': null,
          'selfie-prompt-left-default-value': null,
          'selfie-prompt-left-fallback-i18n-dynamic-variables': null,
          'selfie-prompt-left-fallback-i18n-key': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'selfie-prompt-left-translations': null,
          'selfie-prompt-right-default-value': null,
          'selfie-prompt-right-fallback-i18n-dynamic-variables': null,
          'selfie-prompt-right-fallback-i18n-key': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'selfie-prompt-right-translations': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'selfie-prompt-translations': null,
          'selfie-title-default-value': null,
          'selfie-title-fallback-i18n-dynamic-variables': null,
          'selfie-title-fallback-i18n-key': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'selfie-title-translations': null,
          'stillfie-hint-left-default-value': null,
          'stillfie-hint-left-fallback-i18n-dynamic-variables': null,
          'stillfie-hint-left-fallback-i18n-key': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'stillfie-hint-left-translations': null,
          'stillfie-hint-right-default-value': null,
          'stillfie-hint-right-fallback-i18n-dynamic-variables': null,
          'stillfie-hint-right-fallback-i18n-key': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'stillfie-hint-right-translations': null,
          'stillfie-hint-wrong-default-value': null,
          'stillfie-hint-wrong-fallback-i18n-dynamic-variables': null,
          'stillfie-hint-wrong-fallback-i18n-key': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'stillfie-hint-wrong-translations': null,
          'stillfie-title-default-value': null,
          'stillfie-title-fallback-i18n-dynamic-variables': null,
          'stillfie-title-fallback-i18n-key': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'stillfie-title-translations': null,
          'camera-permissions-btn-cancel-default-value': null,
          'camera-permissions-btn-cancel-fallback-i18n-dynamic-variables': null,
          'camera-permissions-btn-cancel-fallback-i18n-key':
            'inquiry.step.selfie.prompt_page.camera_permissions_btn_cancel',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'camera-permissions-btn-cancel-translations': null,
          'camera-permissions-btn-continue-mobile-default-value': null,
          'camera-permissions-btn-continue-mobile-fallback-i18n-dynamic-variables': null,
          'camera-permissions-btn-continue-mobile-fallback-i18n-key':
            'inquiry.step.selfie.prompt_page.camera_permissions_btn_continue_mobile',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'camera-permissions-btn-continue-mobile-translations': null,
          'camera-permissions-btn-continue-web-default-value': null,
          'camera-permissions-btn-continue-web-fallback-i18n-dynamic-variables': null,
          'camera-permissions-btn-continue-web-fallback-i18n-key':
            'inquiry.step.selfie.prompt_page.camera_permissions_btn_continue_web',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'camera-permissions-btn-continue-web-translations': null,
          'camera-permissions-prompt-default-value': null,
          'camera-permissions-prompt-fallback-i18n-dynamic-variables': null,
          'camera-permissions-prompt-fallback-i18n-key':
            'inquiry.step.selfie.prompt_page.camera_permissions_prompt',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'camera-permissions-prompt-translations': null,
          'camera-permissions-title-default-value': null,
          'camera-permissions-title-fallback-i18n-dynamic-variables': null,
          'camera-permissions-title-fallback-i18n-key':
            'inquiry.step.selfie.prompt_page.camera_permissions_title',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'camera-permissions-title-translations': null,
          'microphone-permissions-btn-cancel-default-value': null,
          'microphone-permissions-btn-cancel-fallback-i18n-dynamic-variables': null,
          'microphone-permissions-btn-cancel-fallback-i18n-key':
            'inquiry.step.selfie.prompt_page.microphone_permissions_btn_cancel',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'microphone-permissions-btn-cancel-translations': null,
          'microphone-permissions-btn-continue-mobile-default-value': null,
          'microphone-permissions-btn-continue-mobile-fallback-i18n-dynamic-variables': null,
          'microphone-permissions-btn-continue-mobile-fallback-i18n-key':
            'inquiry.step.selfie.prompt_page.microphone_permissions_btn_continue_mobile',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'microphone-permissions-btn-continue-mobile-translations': null,
          'microphone-permissions-btn-continue-web-default-value': null,
          'microphone-permissions-btn-continue-web-fallback-i18n-dynamic-variables': null,
          'microphone-permissions-btn-continue-web-fallback-i18n-key':
            'inquiry.step.selfie.prompt_page.microphone_permissions_btn_continue_web',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'microphone-permissions-btn-continue-web-translations': null,
          'microphone-permissions-prompt-default-value': null,
          'microphone-permissions-prompt-fallback-i18n-dynamic-variables': null,
          'microphone-permissions-prompt-fallback-i18n-key':
            'inquiry.step.selfie.prompt_page.microphone_permissions_prompt',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'microphone-permissions-prompt-translations': null,
          'microphone-permissions-title-default-value': null,
          'microphone-permissions-title-fallback-i18n-dynamic-variables': null,
          'microphone-permissions-title-fallback-i18n-key':
            'inquiry.step.selfie.prompt_page.microphone_permissions_title',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'microphone-permissions-title-translations': null,
        },
      },
      ...config,
    },
  };
}

/**
 * Creates mock ui step server config
 */
export function createMockUiStep(
  config?: Partial<InquiryTemplateVersionStepUiConfig>,
  {
    terminal,
    multipleTransitions,
    toStep,
    withFooter,
    workflowVersionId,
    workflowErrorStepName,
  }: TransitionAttributes & MockComponentOptions = {},
  nameDisplay?: string,
): InquiryTemplateVersionStepUi {
  const nameToken = `ui-step-${uniqueId()}`;
  return {
    type: UiStepType.Ui,
    name: nameToken,
    'name-display': nameDisplay,
    token: nameToken,
    config: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'back-step': null,
      components:
        config?.components ??
        createMockUiStepComponents({
          terminal,
          multipleTransitions,
          toStep,
          withFooter,
          workflowVersionId,
          workflowErrorStepName,
        }),
      statuses: terminal
        ? [
            { name: 'completed', initial: false, terminal: true },
            { name: 'approved', initial: false, terminal: true },
          ]
        : [],
      ...config,
    },
  };
}

/**
 * Creates mock ui step client data preview
 */
export function createMockUiStepPreview(
  name?: string,
  config?: Partial<StepUiConfig>,
  previewComponentOptions?: { excludeButton: boolean },
): StepUi {
  return {
    type: UiStepType.Ui,
    name: name || `ui-step-${uniqueId()}`,
    config: {
      'back-step-enabled': false,
      'cancel-button-enabled': false,
      components: createMockUiStepComponentsPreview(previewComponentOptions?.excludeButton),
      localizations: createMockPlatformLocalizations(),
      ...config,
    },
  };
}

function createMockCombinedStepBaseConfig({
  toStep,
  workflowVersionId,
  workflowErrorStepName,
}: TransitionAttributes = {}): CombinedStepBaseConfig {
  return {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    'back-step': null,
    'device-handoff-options': [
      DeviceHandoffOption.Email,
      DeviceHandoffOption.Qr,
      DeviceHandoffOption.Sms,
    ],
    localizations: {},
    statuses: [],
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    'to-step': workflowVersionId ? null : toStep,
    'verification-template-version-id': generateVerificationTemplateVersionId(),
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    'workflow-version-id': toStep ? null : workflowVersionId,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    'workflow-on-error-step-name': toStep ? null : workflowErrorStepName,
  };
}

export function createMockInputTextConfig({
  name = 'input_text',
  config,
}: {
  name?: string;
  config?: Partial<InputTextComponentConfig['config']>;
}): InputTextComponentConfig {
  return {
    name,
    type: UiComponentConfigType.InputText,
    config: {
      field: 'text_field',
      'placeholder-default-value': '',
      ...config,
    },
  };
}

export function createMockInputSelectConfig({
  name = 'input_select',
  config,
}: {
  name?: string;
  config?: Partial<InputSelectComponentConfig['config']>;
}): InputSelectComponentConfig {
  return {
    name,
    type: UiComponentConfigType.InputSelect,
    config: {
      field: 'select_field',
      'placeholder-default-value': '',
      options: [
        {
          value: 'a',
        },
        { value: 'b' },
        { value: 'c' },
      ],
      ...config,
    },
  };
}

export function createMockImageHostedConfig({
  name = 'image_hosted',
  config,
}: {
  name?: string;
  config?: Partial<ImageHostedComponentConfig['config']>;
}): ImageHostedComponentConfig {
  return {
    name,
    type: UiComponentConfigType.ImageHosted,
    config: {
      'asset-id': 'asset-id-1',
      height: '500px',
      width: '500px',
      source: AssetSource.InquiryTemplateVersion,
      alt: 'some alt text',
      ...config,
    },
  };
}

export function createMockInputCheckboxConfig({
  name = 'input_checkbox',
  config,
}: {
  name?: string;
  config?: Partial<InputCheckboxComponentConfig['config']>;
}): InputCheckboxComponentConfig {
  return {
    name,
    type: UiComponentConfigType.InputCheckbox,
    config: {
      field: 'boolean_field',
      'placeholder-default-value': '',
      ...config,
    },
  };
}

export function createMockInputFileUpload({
  name = 'input_file_upload',
  config,
}: {
  name?: string;
  config?: Partial<InputFileUploadComponentConfig['config']>;
}): InputFileUploadComponentConfig {
  return {
    name,
    type: UiComponentConfigType.InputFileUpload,
    config: {
      field: 'text_field',
      'file-size-limit': 30,
      'file-upload-limit': 15,
      'allowed-file-types': [],
      'placeholder-default-value': '',
      ...config,
    },
  };
}

export function createMockInputMaskedText({
  name = 'input_masked_text',
  config,
}: {
  name?: string;
  config?: Partial<InputMaskedTextComponentConfig['config']>;
}): InputMaskedTextComponentConfig {
  return {
    name,
    type: UiComponentConfigType.InputMaskedText,
    config: {
      field: 'string_field',
      'placeholder-default-value': '',
      mask: '',
      ...config,
    },
  };
}

export function createMockInputDateConfig({
  name = 'input_date',
  config,
}: {
  name?: string;
  config?: Partial<InputDateComponentConfig['config']>;
}): InputDateComponentConfig {
  return {
    name,
    type: UiComponentConfigType.InputDate,
    config: {
      field: 'date_field',
      'placeholder-default-value': '',
      'date-range-start': null,
      'date-range-end': null,
      'relative-start-date': null,
      'relative-end-date': null,
      timezone: null,
      ...config,
    },
  };
}

/**
 * Creates mock input_country_select config
 */
export function createMockCountrySelectConfig(): InputCountrySelectComponentConfig {
  return {
    name: 'input_country_select',
    type: UiComponentConfigType.InputCountrySelect,
    config: {
      hidden: null,
      'hidden-server-evaluated': null,
      field: 'selected_country',
      'country-codes': ['US'],
      'placeholder-translations': [],
      'label-translations': [],
      'placeholder-default-value': '',
    },
  };
}

export function createMockInternationalDbComponentConfig(): InputInternationalDbComponentConfig {
  return {
    name: 'input_country_select',
    type: UiComponentConfigType.InputInternationalDb,
    config: {
      hidden: null,
      'hidden-server-evaluated': null,
      'field-key-idb-country': 'idb-country-field-key',
      'field-key-idb-type': 'idb-type-field-key',
      'field-key-idb-value': 'idb-value-field-key',
      'allowed-id-types': [],
      'entity-type': 'individual',
      'hide-country-if-prefilled': false,
      'hide-type-if-prefilled': false,
      'hide-country-if-single-choice': false,
      'hide-type-if-single-choice': false,
    },
  };
}

export function createMockDeviceHandoffLocalizations(): DeviceHandoffLocalizations {
  return {
    'device-handoff-prompt-page': {
      description:
        'To verify your identity, we need access to a camera. We’ll send you a secure link for continuing on your phone. No app download is required.',
      title: 'Continue on another device',
      'qr-code': '',
      'via-email': 'Send Email',
      'via-sms': 'Send SMS',
    },
    'device-handoff-started-page': {
      'btn-resume': 'Continue on this device',
      prompt: 'You’ve started working on another device. Click the button below to continue here.',
      title: 'Continue on other device',
    },
    'device-handoff-email-prompt-page': {
      'btn-submit': 'Send secure link',
      prompt:
        'To verify your identity, we need access to a camera. We’ll email you a secure link for taking photos. No app download is required.',
      title: 'Continue on another device',
    },
    'device-handoff-sms-prompt-page': {
      'btn-submit': 'Send secure link',
      prompt:
        'To verify your identity, we need access to a camera. We’ll SMS you a secure link for taking photos. No app download is required.',
      title: 'Continue on another device',
      disclaimer:
        'For more information, see Persona‘s [Terms of Service](https://withpersona.com/legal/sms-terms-of-use) and [Privacy Policy](https://withpersona.com/legal/privacy-policy). Message and data rates may apply.',
    },
    'device-handoff-email-sent-page': {
      'btn-resend': 'Resend secure link',
      prompt:
        'We’ve sent a secure link to {{client.emailAddress}}. Return to this page after you’ve finished submitting the photos.',
      title: 'Check your device',
    },
    'device-handoff-sms-sent-page': {
      'btn-resend': 'Resend secure link',
      prompt:
        'We’ve sent a secure link to {{client.phoneNumber}}. Return to this page after you’ve finished submitting the photos.',
      title: 'Check your device',
    },
  };
}

/**
 * Creates mock ui step components config
 */
export function createMockUiStepComponents({
  terminal,
  multipleTransitions,
  toStep,
  withFooter,
  workflowVersionId,
  workflowErrorStepName,
}: TransitionAttributes & MockComponentOptions = {}): UiComponentConfig[] {
  const withTransitionComponent = !terminal && (toStep || workflowVersionId);
  const components: UiComponentConfig[] = [
    {
      type: UiComponentConfigType.Title,
      name: 'title-1',
      config: {
        hidden: null,
        'hidden-server-evaluated': null,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'text-translations': null,
        'text-fallback-i18n-key': 'inquiry.ui.step_template.start.title',
      },
      'style-variants': [
        {
          namespace: 'base',
          margin: { top: null, left: null, right: null, bottom: null },
          justify: null,
          'font-family': null,
          'font-size': null,
          'font-weight': null,
          'letter-spacing': null,
          'line-height': null,
          'text-color': null,
          'text-color-highlight': null,
        },
      ],
    },
    {
      type: UiComponentConfigType.Text,
      name: 'text-1',
      config: {
        hidden: null,
        'hidden-server-evaluated': null,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'text-translations': null,
        'text-fallback-i18n-key': 'inquiry.ui.step_template.start.text',
      },
      'style-variants': [
        {
          margin: { top: null, left: null, right: null, bottom: null },
          justify: null,
          'font-family': null,
          'font-size': null,
          'font-weight': null,
          'letter-spacing': null,
          'line-height': null,
          'text-color': null,
          'text-color-highlight': null,
          namespace: 'base',
        },
      ],
    },
  ];

  if (withFooter) {
    components.push({
      type: UiComponentConfigType.Footer,
      name: 'footer-1',
      config: {
        direction: ConfigValueDirection.Horizontal,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        children: withTransitionComponent
          ? [
              {
                type: UiComponentConfigType.ButtonSubmit,
                name: 'button-submit-1',
                config: {
                  hidden: null,
                  'hidden-server-evaluated': null,
                  'to-step': workflowVersionId || terminal ? null : toStep,
                  'workflow-version-id':
                    toStep || terminal ? null : (workflowVersionId ?? generateWorkflowVersionId()),
                  'workflow-on-error-step-name': toStep || terminal ? null : workflowErrorStepName,
                  'text-translations': null,
                  'text-fallback-i18n-key': 'inquiry.ui.step_template.start.button_submit_1',
                },
              },
            ]
          : [
              {
                type: UiComponentConfigType.Text,
                name: 'text-2',
                config: {
                  hidden: null,
                  'hidden-server-evaluated': null,
                  'text-translations': null,
                  'text-fallback-i18n-key': 'inquiry.ui.step_template.start.text',
                },
              },
            ],
      },
    });
  } else {
    if (withTransitionComponent) {
      components.push({
        type: UiComponentConfigType.ButtonSubmit,
        name: 'button-submit-1',
        config: {
          hidden: null,
          'hidden-server-evaluated': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'to-step': workflowVersionId || terminal ? null : toStep,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'workflow-version-id':
            toStep || terminal ? null : (workflowVersionId ?? generateWorkflowVersionId()),
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'workflow-on-error-step-name': toStep || terminal ? null : workflowErrorStepName,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'text-translations': null,
          'text-fallback-i18n-key': 'inquiry.ui.step_template.start.button_submit_1',
        },
      });
    }
  }

  if (multipleTransitions) {
    components.push({
      type: UiComponentConfigType.ButtonSubmit,
      name: 'button-submit-2',
      config: {
        hidden: null,
        'hidden-server-evaluated': null,
        'to-step': 'inconsequential_step',
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'workflow-version-id': null,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'workflow-on-error-step-name': null,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'text-translations': null,
        'text-fallback-i18n-key': 'inquiry.ui.step_template.start.button_submit_1',
      },
    });
  }

  return components;
}

/**
 * Creates mock ui step components client data preview
 */
export function createMockUiStepComponentsPreview(excludeButton = false): UiComponent[] {
  const base = [
    {
      type: UiComponentType.Title,
      name: 'title-1',
      attributes: {
        text: 'title',
      },
    },
    {
      type: UiComponentType.Text,
      name: 'text-1',
      attributes: {
        text: 'text',
      },
    },
    {
      type: UiComponentType.ButtonSubmit,
      name: 'button-submit-1',
      attributes: {
        'button-type': ButtonType.Primary,
        text: 'continue',
      },
    },
  ];

  if (excludeButton) {
    base.pop();
  }

  return base as UiComponent[];
}

function createMockPartialInquiryTemplateVersion(
  attributes?: Partial<PartialInquiryTemplateVersionAttributes>,
  _relationships?: null,
  id?: string,
): PartialInquiryTemplateVersion {
  return {
    attributes: {
      name: 'My cool template version',
      description: 'Edited by Tim',
      'name-display': 'My cool display template version',
      'last-updater': 'Tim Hwang',
      tier: InquiryTemplateVersionTier.Basic,
      status: InquiryTemplateVersionStatus.Published,
      live: true,
      ...attributes,
    },
    id: id || generateInquiryTemplateVersionId(),
    type: ApiModel.PartialInquiryTemplateVersion,
  };
}

export function createMockPartialInquiryTemplateVersionFromVersion(
  inquiryTemplateVersion: InquiryTemplateVersion,
): PartialInquiryTemplateVersion {
  return {
    attributes: {
      name: inquiryTemplateVersion.attributes.name,
      description: '',
      'name-display': inquiryTemplateVersion.attributes['name-display'],
      'last-updater': '',
      tier: InquiryTemplateVersionTier.Basic,
      status: inquiryTemplateVersion.attributes.status,
      // FIXME: this isn't correct but works for the places where we need this
      live: inquiryTemplateVersion.attributes.status === InquiryTemplateVersionStatus.Published,
    },
    id: inquiryTemplateVersion.id,
    type: ApiModel.PartialInquiryTemplateVersion,
  };
}

export function createMockDraftPartialInquiryTemplateVersion(
  attributes?: Partial<PartialInquiryTemplateVersionAttributes>,
  id?: string,
): PartialInquiryTemplateVersion {
  return createMockPartialInquiryTemplateVersion(
    {
      ...attributes,
      live: false,
      status: InquiryTemplateVersionStatus.Draft,
      'updated-at': '2020-02-16T09:31:58.000Z',
    },
    null,
    id,
  );
}

export function createMockPublishedPartialInquiryTemplateVersion(
  attributes?: Partial<PartialInquiryTemplateVersionAttributes>,
  id?: string,
): PartialInquiryTemplateVersion {
  return createMockPartialInquiryTemplateVersion(
    {
      ...attributes,
      live: true,
      status: InquiryTemplateVersionStatus.Published,
      'updated-at': '2020-02-15T09:31:58.000Z',
    },
    null,
    id,
  );
}

// Version diff stubs
const templateId = 'itmpl_123';
const workflowId = 'wfl_456';
const verificationTemplateId = 'vtmpl_789';

export const emptySchema = {
  verificationTemplateVersions: {},
  workflowVersions: {},
  matchRequirementProfiles: {},
};

export const getMockComparedVersions = (): {
  currentVersion: InquiryTemplateVersion;
  comparedVersion: InquiryTemplateVersion;
} => {
  return {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    currentVersion: createMockInquiryTemplateVersion(null, {
      'inquiry-template': {
        data: {
          id: templateId,
          type: ApiModel.InquiryTemplate,
        },
      },
    }),
    comparedVersion: createMockInquiryTemplateVersion(
      {
        name: 'Old reliable published version',
      },
      {
        'inquiry-template': {
          data: {
            id: templateId,
            type: ApiModel.InquiryTemplate,
          },
        },
      },
    ),
  };
};

export const getMockComparedSchemas = (): {
  currentSchema: InquiryTemplateSchema;
  comparedSchema: InquiryTemplateSchema;
} => {
  const currentWorkflowVersion = createMockWorkflowVersionV2(
    { 'steps-config': [createMockActionStep()] },
    {
      workflow: {
        data: {
          id: workflowId,
          type: ApiModel.Workflow,
        },
      },
    },
  );
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const currentVtmplv = createMockVerificationTemplateVersionGovernmentId(null, {
    'verification-template': {
      data: {
        id: verificationTemplateId,
        type: ApiModel.VerificationTemplate,
      },
    },
  });
  const comparedWorkflowVersion = createMockWorkflowVersionV2(
    { 'steps-config': [createMockActionStep(), createMockActionStep(), createMockActionStep()] },
    {
      workflow: {
        data: {
          id: workflowId,
          type: ApiModel.Workflow,
        },
      },
    },
  );
  const comparedVtmplv = createMockVerificationTemplateVersionGovernmentId(
    {
      'device-handoff-options': [DeviceHandoffOption.Email, DeviceHandoffOption.Sms],
    },
    {
      'verification-template': {
        data: {
          id: verificationTemplateId,
          type: ApiModel.VerificationTemplate,
        },
      },
    },
  );
  return {
    currentSchema: {
      verificationTemplateVersions: {
        [currentVtmplv.id]: currentVtmplv,
      },
      workflowVersions: {
        [currentWorkflowVersion.id]: currentWorkflowVersion,
      },
      matchRequirementProfiles: {},
    },
    comparedSchema: {
      verificationTemplateVersions: {
        [comparedVtmplv.id]: comparedVtmplv,
      },
      workflowVersions: {
        [comparedWorkflowVersion.id]: comparedWorkflowVersion,
      },
      matchRequirementProfiles: {},
    },
  };
};

export const createMockInquiryTemplateVersionErrors = (): InquiryTemplateVersionError[] => {
  return [
    {
      'error-message': 'Mock error message that blocks save and has no entities',
      'blocking-save': true,
    },
    {
      'error-message': 'Mock error message that does not block save and has no entities',
      'blocking-save': false,
    },
    {
      'error-message': 'Mock error message that does not block save and has a ui component entity',
      'blocking-save': false,
      'relevant-entities': [
        {
          'entity-type': 'ui_component',
          name: 'mock_ui_component_name',
          'step-name': 'mock_step_name',
        } as UiComponentErrorEntity,
      ],
    },
    {
      'error-message': 'Mock error message that does not block save and has a step entity',
      'blocking-save': false,
      'relevant-entities': [
        {
          'entity-type': 'step',
          name: 'mock_step_name',
        } as StepErrorEntity,
      ],
    },
    {
      'error-message': 'Mock error message that does not block save and has a wfv step entity',
      'blocking-save': false,
      'relevant-entities': [
        {
          'entity-type': 'workflow_version_step',
          token: 'mock_wfv_step_token',
          'workflow-version-token': 'mock_wfv_token',
        } as WorkflowVersionStepErrorEntity,
      ],
    },
    {
      'error-message': 'Mock error message that does not block save and has a wfv entity',
      'blocking-save': false,
      'relevant-entities': [
        {
          'entity-type': 'workflow_version',
          token: 'mock_wfv_token',
        } as WorkflowVersionErrorEntity,
      ],
    },
    {
      'error-message': 'Mock error message that does not block save and has a field schema entity',
      'blocking-save': false,
      'relevant-entities': [
        {
          'entity-type': 'field_schema',
          key: 'mock_field_schema',
        } as FieldSchemaErrorEntity,
      ],
    },
  ];
};
